import React, { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import { COLORS } from '../styles/colors';
import Pagination from './Pagination';
import ProjectCard, {
  ProjectCardSkeleton,
} from './ProjectCard';
import TextInput from './TextInput';
import { TURKIYE_ALTERNATIVES } from '../utils/countrySpellingAlternatives';
import { INITIATIVES_LIST_TITLE, SEARCH_PLACEHOLDER } from '../constants/uiText';
import ExportCsvButton from './ExportCsvButton';
import SortDropdownButton from './SortDropdownButton';
import { SortOptionId } from '../utils/sortOptions';

interface IProps {
  page: number;
  projects: any[];
  pages: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  onExportCsv: () => void;
  exportDisabled?: boolean;
  exporting?: boolean;
  sortOptions?: Array<{ id: SortOptionId; label: string }>
  selectedSort?: SortOptionId; 
  onSortChange?: (id: SortOptionId) => void;
}

const ProjectSection = ({
  page,
  projects,
  pages,
  setPage,
  loading,
  searchTerm,
  setSearchTerm,
  onExportCsv,
  exportDisabled,
  exporting,
  sortOptions,
  selectedSort,
  onSortChange,
}: IProps) => {
  return (
    <>
      <div
        style={{
          marginTop: '2rem',
          marginBottom: '1rem',
          borderBottom: `1px solid ${COLORS.Border}`,
          padding: '1rem 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
        id="initiatives"
      >
        <h2 style={{ margin: 0 }}>{INITIATIVES_LIST_TITLE}</h2>
        <ActionsContainer>
          <SortAction><SortDropdownButton options={sortOptions} selected={selectedSort} onChange={onSortChange} /></SortAction>
          <ExportAction><ExportCsvButton onExport={onExportCsv} disabled={!!exportDisabled}  exporting={!!exporting} /></ExportAction>
        </ActionsContainer>
      </div>
      {/*
        Special case: If the user types any of the Turkey alternatives,
        we normalize the searchTerm to 'Türkiye' for search purposes,
        but keep the input value as the user typed.
      */}
      <TextInput
        clearControl={true}
        placeholder={SEARCH_PLACEHOLDER}
        value={searchTerm}
        setState={(val: string) => {
          const normalized = val.trim().toLowerCase();
          // Also match the canonical 'Türkiye' spelling
          const allTurkiyeAlternatives = [
            ...TURKIYE_ALTERNATIVES,
            'türkiye',
          ];
          if (
            allTurkiyeAlternatives.some(
              (alt) => normalized === alt
            )
          ) {
            setSearchTerm('Türkiye');
            return;
          }
          setSearchTerm(val);
        }}
      />
      <div style={{ marginTop: '1rem' }}>
        <ProjectCardGrid>
          {!loading ? (
            <>
              {projects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                />
              ))}
            </>
          ) : (
            <>
              {new Array(12).fill('').map((_, i) => (
                <ProjectCardSkeleton key={i} />
              ))}
            </>
          )}
        </ProjectCardGrid>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            marginTop: '1rem',
          }}
        >
          {page !== 0 && (
            <Pagination
              currentPage={page}
              pages={pages}
              changePage={setPage}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ProjectSection;

const ProjectCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;

  @media (max-width: 574px) {
    grid-template-columns: 1fr;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 574px) {
    flex-direction: column;
    align-items: stretch;
  }

  & button {
    width: 100%
  }
`;

const SortAction = styled.div`
  order: 1;

  @media (max-width: 574px) {
    order: 2; /* below Export on mobile */
    width: 100%;
  }
`;

const ExportAction = styled.div`
  order: 2;

  @media (max-width: 574px) {
    order: 1; /* above Sort on mobile */
    width: 100%;
  }
`;


